'use client';

import * as React from 'react';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table';
import type { ColumnDef, ColumnFiltersState, SortingState, VisibilityState, Table as TanStackTable } from '@tanstack/react-table';

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DataTablePagination } from './pagination';
import { cn } from '@/lib/utils';

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  // Configuration options
  enableSorting?: boolean;
  enableFiltering?: boolean;
  enablePagination?: boolean;
  enableColumnVisibility?: boolean;
  enableRowSelection?: boolean;
  enableTableHeader?: boolean;
  // Pagination options
  pageSize?: number;
  pageSizeOptions?: number[];
  // Filter options
  filterColumn?: string;
  filterPlaceholder?: string;
  // Server-side sorting — when set, the table stops sorting rows itself and defers to
  // whatever order `data` already arrives in, reporting sort changes via onSortingChange
  // instead. Used by tables backed by a paginated API (e.g. the Sources SOP table) where
  // sorting has to happen in the query, not after only the current page has been fetched.
  manualSorting?: boolean;
  sorting?: SortingState;
  onSortingChange?: (sorting: SortingState) => void;
  // Callbacks
  onRowClick?: (row: TData) => void;
  onRowSelectionChange?: (selectedRows: TData[]) => void;
  getRowClassName?: (row: TData) => string;
  // Custom toolbar
  toolbar?: (table: TanStackTable<TData>) => React.ReactNode;
  // Empty state
  emptyMessage?: string;
  // Loading state
  isLoading?: boolean;
  // Shown in place of the 'Loading...' text while loading, e.g. a spinner
  loadingContent?: React.ReactNode;
  // Custom className
  className?: string;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  enableSorting = true,
  enableFiltering = false,
  enablePagination = true,
  enableColumnVisibility = false,
  enableRowSelection = false,
  enableTableHeader = true,
  pageSize = 10,
  pageSizeOptions = [10, 20, 30, 40, 50],
  filterColumn,
  filterPlaceholder = 'Filter...',
  manualSorting = false,
  sorting: controlledSorting,
  onSortingChange,
  onRowClick,
  onRowSelectionChange,
  getRowClassName,
  toolbar,
  emptyMessage = 'No results.',
  isLoading = false,
  loadingContent = 'Loading...',
  className
}: DataTableProps<TData, TValue>) {
  const [internalSorting, setInternalSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});

  const isSortingControlled = manualSorting && controlledSorting !== undefined && !!onSortingChange;
  const sorting = isSortingControlled ? controlledSorting : internalSorting;
  const handleSortingChange: React.Dispatch<React.SetStateAction<SortingState>> = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    if (isSortingControlled) onSortingChange(next);
    else setInternalSorting(next);
  };

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    // Column sizing
    columnResizeMode: 'onChange',
    defaultColumn: {
      minSize: 30,
      maxSize: 10000
    },
    // Sorting
    ...(enableSorting && {
      onSortingChange: handleSortingChange,
      ...(manualSorting ? { manualSorting: true } : { getSortedRowModel: getSortedRowModel() })
    }),
    // Filtering
    ...(enableFiltering && {
      onColumnFiltersChange: setColumnFilters,
      getFilteredRowModel: getFilteredRowModel()
    }),
    // Pagination
    ...(enablePagination && {
      getPaginationRowModel: getPaginationRowModel(),
      initialState: {
        pagination: {
          pageSize
        }
      }
    }),
    // Column visibility
    ...(enableColumnVisibility && {
      onColumnVisibilityChange: setColumnVisibility
    }),
    // Row selection
    ...(enableRowSelection && {
      onRowSelectionChange: setRowSelection,
      enableRowSelection: true
    }),
    state: {
      ...(enableSorting && { sorting }),
      ...(enableFiltering && { columnFilters }),
      ...(enableColumnVisibility && { columnVisibility }),
      ...(enableRowSelection && { rowSelection })
    }
  });

  // Handle row selection change callback
  React.useEffect(() => {
    if (enableRowSelection && onRowSelectionChange) {
      const selectedRows = table.getFilteredSelectedRowModel().rows.map((row) => row.original);
      onRowSelectionChange(selectedRows);
    }
  }, [rowSelection, enableRowSelection, onRowSelectionChange, table]);

  return (
    <div className={className}>
      {/* Toolbar */}
      {toolbar && <div className='mb-4'>{toolbar(table)}</div>}

      {/* Table */}
      <Table style={{ tableLayout: 'fixed', width: '100%' }}>
        {enableTableHeader && (
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      key={header.id}
                      // Exposed so callers can target a specific column in CSS
                      // (e.g. to let one column absorb surplus width) without
                      // relying on a brittle :nth-child position.
                      data-column-id={header.column.id}
                      style={{ width: `${header.getSize()}px` }}
                    >
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
        )}
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={columns.length} className='h-48 text-center'>
                {loadingContent}
              </TableCell>
            </TableRow>
          ) : table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() && 'selected'}
                onClick={() => onRowClick?.(row.original)}
                className={cn(onRowClick && 'cursor-pointer', getRowClassName?.(row.original))}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    data-column-id={cell.column.id}
                    style={{ width: `${cell.column.getSize()}px` }}
                    className='overflow-hidden'
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className='h-80 text-center text-xs'>
                {emptyMessage}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {/* Pagination */}
      {enablePagination && !isLoading && data.length > 0 && (
        <div className='mt-4'>
          <DataTablePagination table={table} pageSizeOptions={pageSizeOptions} showRowSelection={enableRowSelection} />
        </div>
      )}
    </div>
  );
}
